import { useState, type KeyboardEvent } from 'react';
import { useActiveEntity, type PortalEntityOption } from '../hooks/useActiveEntity';

function label(o: PortalEntityOption): string {
  return o.entityType && o.entityType !== 'Unknown' ? `${o.displayName} · ${o.entityType}` : o.displayName;
}

/**
 * Spec §7 — the active entity, in the topbar. A plain label for a single-entity user; otherwise a native select
 * plus an explicit **Switch** button.
 *
 * Choosing an option only picks it. On a closed native select a single ArrowDown fires `change`, so committing on
 * `change` would make browsing the options with the keyboard an audited switch. The switch happens only on the
 * button, or Enter in the select. Neither control is ever `disabled`: disabling the focused control drops focus to
 * the page body (WCAG 2.4.3), so a pending switch is shown with `aria-busy` and further requests are ignored.
 * Long names truncate rather than widen the bar.
 */
export function EntitySwitcher() {
  const { options, active, switchTo, isSwitching } = useActiveEntity();
  // The pending choice, remembered against the entity it was made from: when the active entity changes (a switch
  // completes, or a revoked grant falls back) the select shows the active entity again without an effect.
  const [picked, setPicked] = useState<{ from: string; value: string } | null>(null);

  if (!active) return null;

  if (options.length <= 1) {
    return (
      <span data-testid="entity-label"
        className="sm:ml-3 min-w-0 max-w-full sm:max-w-[min(40vw,320px)] truncate text-sm font-medium text-[var(--text1)]">
        {label(active)}
      </span>
    );
  }

  const choice = picked && picked.from === active.clientRef ? picked.value : active.clientRef;
  const unchanged = choice === active.clientRef;

  const commit = () => {
    if (isSwitching || unchanged) return;
    void switchTo(choice).then((switched) => {
      // Refused or failed: show the entity actually in force again, not the one that was asked for.
      if (!switched) setPicked(null);
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  };

  return (
    <div className="sm:ml-3 flex w-full min-w-0 items-center gap-2 sm:w-auto" aria-busy={isSwitching}>
      <label htmlFor="entity-switcher" className="sr-only">Viewing entity</label>
      <select
        id="entity-switcher"
        value={choice}
        onChange={(e) => setPicked({ from: active.clientRef, value: e.target.value })}
        onKeyDown={onKeyDown}
        className="h-11 min-w-0 flex-1 sm:flex-initial sm:max-w-[min(34vw,320px)] truncate rounded-md border border-[var(--border)] bg-[var(--surface)] px-2 text-sm font-medium text-[var(--text1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
      >
        {options.map((o) => <option key={o.clientRef} value={o.clientRef}>{label(o)}</option>)}
      </select>
      <button
        type="button"
        onClick={commit}
        aria-disabled={unchanged || isSwitching}
        className={`h-11 shrink-0 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm font-medium text-[var(--text1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${unchanged || isSwitching ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[var(--surface2)]'}`}
      >
        {isSwitching ? 'Switching…' : 'Switch'}
      </button>
    </div>
  );
}
